import React, {
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
} from "react";
import {
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  MenuItem,
  Skeleton,
  TableCell,
  TableRow,
  Table,
  TableBody,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SnackbarAlert from "../SnackbarAlert";
import nucleusxp from "nucleusxp";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

const Material = forwardRef(
  ({ formData, handleChange, highlighted = false }, ref) => {
    const [materialList, setMaterialList] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState(0);
    const [selectedMaterial, setSelecedMaterial] = useState(null);
    const [snackbar, setSnackbar] = useState({
      open: false,
      severity: "error",
      message: "",
    });

    const textFieldRef = useRef();
    useImperativeHandle(ref, () => ({
      scrollToField: () => {
        textFieldRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      },
    }));

    useEffect(() => {
      const fetchBaseData = async () => {
        setLoading(true);
        const materialData = await nucleusxp.healthSafety.getMaterialTypes();
        setMaterialTypes(materialData.items ?? []);
        setLoading(false);
      };
      fetchBaseData();
    }, []);

    useEffect(() => {
      if (
        Array.isArray(formData.materialList) &&
        formData.materialList.length > 0
      ) {
        setMaterialList(formData.materialList);
      }
    }, [formData.materialList, setMaterialList]);
    const handleClose = (event, reason) => {
      if (reason === "clickaway") return;
      setSnackbar({ ...snackbar, open: false });
    };

    const handleAddMaterial = () => {
      if (!formData.materialType) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Please check Type of Material field",
        });
        return;
      }
      if (!formData.materialName) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Please check Material name field",
        });
        return;
      }
      const newData = {
        type: formData.materialType || "",
        name: formData.materialName || "",
        idSQ: formData.materialIdSQ || "",
      };
      setMaterialList((prev) => {
        const updatedList = [...prev, newData];
        formData.materialList = updatedList;
        return updatedList;
      });

      if (formData.materialList.length === 1) {
        setSelecedMaterial(newData);
      }

      formData.materialType = "";
      formData.materialName = "";
      formData.materialIdSQ = "";

      // handleChange({ target: { name: "materialType", value: "" } });
      // handleChange({ target: { name: "materialName", value: "" } });
      // handleChange({ target: { name: "materialIdSQ", value: "" } });
    };

    const handleRemoveItem = (index) => {
      setMaterialList((prev) => prev.filter((_, i) => i !== index));
    };

    const handleListItemClick = (item, index) => {
      if (expandedIndex === index) {
        setExpandedIndex(null);
        setSelecedMaterial(null);
      } else {
        setExpandedIndex(index);
        setSelecedMaterial(item);
      }
    };

    return (
      <div
        ref={textFieldRef}
        name="materialList"
        style={{ border: highlighted ? "2px solid #1976d2" : "none" }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Material Damages
          </Typography>
          <Box>
            {!loading ? (
              <TextField
                fullWidth
                margin="normal"
                label="Type of Material"
                name="materialType"
                select
                value={formData.materialType || ""}
                onChange={handleChange}
              >
                {materialTypes.map((material) => (
                  <MenuItem value={material.id} key={material.id}>
                    {material.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Skeleton variant="rectangular" width={350} height={30} />
            )}
          </Box>

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="materialName"
            value={formData.materialName || ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="ID/SKU"
            name="materialIdSQ"
            value={formData.materialIdSQ || ""}
            onChange={handleChange}
          />
          <IconButton onClick={handleAddMaterial} color="primary">
            <AddIcon />
          </IconButton>

          <List>
            {materialList.map((item, index) => (
              <ListItem
                key={index}
                onClick={() => handleListItemClick(item, index)}
                sx={{
                  backgroundColor:
                    expandedIndex === index ? "lightblue" : "white",
                  cursor: "pointer",
                  border:
                    expandedIndex === index ? "2px solid #007BFF" : "none",
                  "&:hover": {
                    backgroundColor:
                      expandedIndex === index ? "lightblue" : "#f5f5f5",
                  },
                }}
              >
                <ListItemText primary={`${item.name} - ${item.idSQ}`} />
                <IconButton sx={{ marginLeft: "auto" }}>
                  {expandedIndex === index ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          {selectedMaterial && (
            <Box sx={{ marginTop: "20px" }}>
              <Typography variant="h6">Selected Material Details</Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>
                      {
                        materialTypes.find(
                          (material) => material.id === selectedMaterial.type,
                        ).name
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>{selectedMaterial.name || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>ID/SKU</TableCell>
                    <TableCell>{selectedMaterial.idSQ || "N/A"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
          <SnackbarAlert
            open={snackbar.open}
            onClose={handleClose}
            severity={snackbar.severity}
            message={snackbar.message}
          />
        </Box>
      </div>
    );
  },
);

export default Material;
